import { useMemo } from 'react';

import { useMarketSelectorAggregationPresets } from '@src/components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorItem, MarketSelectorValue } from '@src/components/MarketSelector/types';
import { useMarketSelector } from '@src/components/MarketSelector/useMarketSelector';
import { useCallbackRef } from '@talos/kyoko';

export interface PredictionCardMarketSelectorParams {
  items: MarketSelectorItem[];
  selectedMarketAccounts: string[];
  /** Trading aggregation backing the current selection, if it came from one. */
  selectedAggregation: string | undefined;
  onChangeSelectedMarketAccounts: (accounts: string[], aggregation: string | undefined) => void;
}

/**
 * Prediction cards always use the new MarketSelector, with trading aggregations available like
 * regular market cards. Aggregation presets resolve to market accounts, so a selected aggregation
 * is persisted alongside the accounts it selected and cleared as soon as the user picks manually.
 */
export function usePredictionCardMarketSelector({
  items,
  selectedMarketAccounts,
  selectedAggregation,
  onChangeSelectedMarketAccounts,
}: PredictionCardMarketSelectorParams) {
  const { aggregationPresets, aggregationPresetsByID } = useMarketSelectorAggregationPresets();

  // Until the presets have loaded (or if the saved aggregation was deleted) the ID cannot resolve to
  // a preset, so keep the accounts it selected rather than showing an unresolvable preset.
  const value: MarketSelectorValue = useMemo(
    () =>
      selectedAggregation && aggregationPresetsByID?.has(selectedAggregation)
        ? { type: 'preset', presetID: selectedAggregation, selections: selectedMarketAccounts }
        : { type: 'custom', selections: selectedMarketAccounts },
    [aggregationPresetsByID, selectedAggregation, selectedMarketAccounts]
  );

  const handleChange = useCallbackRef((newValue: MarketSelectorValue) => {
    onChangeSelectedMarketAccounts(newValue.selections, newValue.type === 'preset' ? newValue.presetID : undefined);
  });

  return useMarketSelector({
    value,
    onChange: handleChange,
    items,
    presets: aggregationPresets,
  });
}
